import React from "react";
import { Link } from "react-router-dom";
import { Lock, Sparkles, ArrowRight } from "lucide-react";

export function Overline({ children, className = "" }) {
  const color = className.includes("text-") ? "" : "text-nm-coral";
  return <div className={`text-[11px] font-semibold uppercase tracking-[0.2em] ${color} ${className}`}>{children}</div>;
}

export function ProgressBar({ pct, plum = false }) {
  return (
    <div className="h-2 w-full rounded-full bg-nm-primary/10 overflow-hidden">
      <div className={`h-full rounded-full transition-[width] duration-500 ${plum ? "bg-nm-plum" : "bg-nm-primary"}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="flex items-center justify-center py-24 text-nm-neutral" data-testid="loading">
      <div className="h-5 w-5 rounded-full border-2 border-nm-primary/30 border-t-nm-primary animate-spin mr-3" />
      {label}
    </div>
  );
}

export function LockCard({ part, teaser, points = [], title }) {
  const isPart2 = part === "part_2";
  const accent = isPart2 ? "plum" : "primary";
  return (
    <div data-testid="lock-card" className={`relative overflow-hidden rounded-3xl border p-8 sm:p-10 ${isPart2 ? "border-nm-plum/20 bg-nm-plum/[0.04]" : "border-nm-primary/15 bg-nm-primary/[0.03]"}`}>
      <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${isPart2 ? "bg-nm-plum/12 text-nm-plum" : "bg-nm-primary/12 text-nm-primary"}`}>
        <Lock size={13} /> Locked
      </div>
      {title && <h3 className="font-serif text-3xl text-nm-ink mt-4">{title}</h3>}
      <p className="text-nm-neutral mt-3 max-w-xl leading-relaxed">{teaser}</p>
      {points.length > 0 && (
        <ul className="mt-6 space-y-2.5 max-w-xl">
          {points.map((p, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-nm-ink/80">
              <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${isPart2 ? "bg-nm-plum" : "bg-nm-primary"}`} />
              {p}
            </li>
          ))}
        </ul>
      )}
      <Link to="/app/account#upgrade" data-testid="unlock-cta"
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-nm-coral px-6 py-3 text-sm font-medium text-white hover:brightness-105 hover:-translate-y-0.5 transition-[filter,transform] duration-200">
        <Sparkles size={16} /> {isPart2 ? "Unlock Part 2" : "Unlock Part 1"} <ArrowRight size={16} />
      </Link>
    </div>
  );
}

export function UpgradeBanner({ entitlements }) {
  if (entitlements.bundle_owner) return null;
  let msg, cta;
  if (entitlements.has_part_1 && !entitlements.has_part_2) {
    msg = "Part 2 is available when you're ready to build the human side of the role.";
    cta = "Add Part 2 or upgrade to the Bundle";
  } else if (entitlements.has_part_2 && !entitlements.has_part_1) {
    msg = "Part 1 covers the systems, structure, and templates that support the role.";
    cta = "Add Part 1 or upgrade to the Bundle";
  } else {
    msg = "Unlock the full system — foundations and the human side of management.";
    cta = "See pricing";
  }
  return (
    <div data-testid="upgrade-banner" className="relative overflow-hidden rounded-3xl bg-nm-primary p-7 sm:p-9 text-white nm-shadow-md">
      <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-nm-coral/25 blur-2xl" />
      <div className="absolute -right-6 bottom-0 h-32 w-32 rounded-full bg-nm-highlight/20 blur-2xl" />
      <div className="relative">
        <Overline className="text-nm-highlight">Unlock the full system</Overline>
        <p className="font-serif text-2xl sm:text-3xl mt-3 max-w-xl leading-snug">{msg}</p>
        <Link to="/app/account#upgrade" data-testid="banner-upgrade-btn"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-nm-coral px-6 py-3 text-sm font-medium hover:brightness-105 hover:-translate-y-0.5 transition-[filter,transform] duration-200">
          <Sparkles size={16} /> {cta} <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}
